// Apolo IDE — S4 FROZEN CONTRACTS (Auto-review · Permissions). Phase B baseline.
//
// Sessão 4/5 do sprint de REDESIGN (Checkpointing + Auto-review). Estes shapes
// são CONGELADOS após Phase B (tag `s4-arch-baseline`). JS puro com JSDoc (o
// projeto não tem TS); validadores leves. Sem I/O, sem imports — domínio puro
// (Dependency Rule), igual ao s5Contracts.js / injectionBudget do Worker.
//
// Consumido por: lib/permissions.js (loader do .apollo/permissions.json),
// lib/autoReview.js (gate pré-tool no agentLoop) e a UI do S4.
//
// LEIS embutidas aqui (testáveis):
//  - Auto-review é INTERMEDIÁRIO: ele gateia tool calls, mas NUNCA substitui o
//    honest-green como veredito FINAL (anti-reward-hacking; risk #2).
//  - DENY-BY-DEFAULT: policy ausente/ambígua → tool call destrutiva é 'block'
//    (ou 'ask'), nunca 'allow' silencioso (risk #5).
//  - O classifier é READ-ONLY: inspeciona, não muta. Só a família read-only pode
//    ser auto-allowed sem policy explícita.

// ───────────────────────── Auto-review ─────────────────────────

/** @typedef {'allow'|'block'|'ask'} AutoReviewDecision */
export const AUTO_REVIEW_DECISIONS = Object.freeze(['allow', 'block', 'ask']);

/**
 * @typedef {Object} AutoReviewResult
 * @property {AutoReviewDecision} decision
 * @property {string} reason            Explicação que volta ao agente (feedback loop)
 */

/** @param {unknown} v @returns {v is AutoReviewDecision} */
export function isAutoReviewDecision(v) {
  return typeof v === 'string' && AUTO_REVIEW_DECISIONS.includes(v);
}

/** @param {unknown} v @returns {v is AutoReviewResult} */
export function isAutoReviewResult(v) {
  if (!v || typeof v !== 'object') return false;
  const r = /** @type {Record<string, unknown>} */ (v);
  return isAutoReviewDecision(r.decision) && typeof r.reason === 'string';
}

// ───────────────────────── Tool families ─────────────────────────
// Nomes reais do agentTools.js (renderer). Read-only = auto-allowed sem policy;
// destrutivas = exigem allow (deny-by-default). Qualquer nome DESCONHECIDO é
// tratado como destrutivo (deny-by-default), por LEI.

/** Família read-only (a única auto-allowed sem policy explícita). */
export const READ_ONLY_TOOLS = Object.freeze([
  'read_file', 'list_files', 'grep_search', 'semantic_search', 'search_brain',
]);

/** Família destrutiva (muta estado — exige allow; deny-by-default). */
export const DESTRUCTIVE_TOOLS = Object.freeze([
  'run_command', 'write_file', 'apply_edit',
]);

/** @param {unknown} name @returns {boolean} */
export function isReadOnlyTool(name) {
  return typeof name === 'string' && READ_ONLY_TOOLS.includes(name);
}

/**
 * Destrutivo = está na lista destrutiva OU é desconhecido (não read-only).
 * LEI (deny-by-default): tool desconhecida conta como destrutiva.
 * @param {unknown} name @returns {boolean}
 */
export function isDestructiveTool(name) {
  if (typeof name !== 'string' || !name) return true; // sem nome → trate como destrutivo
  if (DESTRUCTIVE_TOOLS.includes(name)) return true;
  return !READ_ONLY_TOOLS.includes(name); // desconhecida → destrutiva
}

// ───────────────────────── Permissions (.apollo/permissions.json) ─────────────────────────

/**
 * @typedef {Object} AutoRunPolicy
 * @property {string[]} allow_instructions   Regras NL de allow
 * @property {string[]} block_instructions   Regras NL de block
 */
/**
 * @typedef {Object} Permissions
 * @property {AutoRunPolicy} autoRun
 */

/** Policy default — vazia = deny-by-default em destrutivas. Frozen. */
export const DEFAULT_PERMISSIONS = Object.freeze({
  autoRun: Object.freeze({
    allow_instructions: Object.freeze([]),
    block_instructions: Object.freeze([]),
  }),
});

/** @param {unknown} v @returns {v is Permissions} */
export function isPermissions(v) {
  if (!v || typeof v !== 'object') return false;
  const p = /** @type {Record<string, unknown>} */ (v);
  const a = p.autoRun;
  if (!a || typeof a !== 'object') return false;
  const ar = /** @type {Record<string, unknown>} */ (a);
  return Array.isArray(ar.allow_instructions) && Array.isArray(ar.block_instructions)
    && ar.allow_instructions.every((s) => typeof s === 'string')
    && ar.block_instructions.every((s) => typeof s === 'string');
}

/** Normalize a tool-schema `block:[{tool,match,reason}]` array → structured block rules. */
function cleanBlockRules(arr) {
  return (Array.isArray(arr) ? arr : [])
    .filter((e) => e && typeof e === 'object' && typeof e.tool === 'string' && e.tool.trim().length > 0)
    .map((e) => ({
      tool: e.tool,
      match: typeof e.match === 'string' && e.match.length > 0 ? e.match : undefined,
      reason: typeof e.reason === 'string' && e.reason.length > 0 ? e.reason : undefined,
    }));
}

/** Coerce a default_deny value to strict boolean|undefined (undefined ⇒ deny-by-default). */
function coerceDefaultDeny(v) {
  if (v === false) return false;
  if (v === true) return true;
  return undefined;
}

/**
 * Boundary validator + normalizer for parsed permissions.json.
 *
 * Understands TWO schemas (WS-D / INV-5 — incidente tastedesign):
 *   1. Renderer NL-schema (legacy):  { autoRun: { allow_instructions[], block_instructions[] } }
 *   2. Worker/canonical tool-schema: { default_deny, allow:[{tool}], ask:[{tool}], block:[{tool,match}] }
 *      — este é o `.apolo/permissions.json` que SHIPPA no repo. Antes o renderer não o
 *      entendia e colapsava tudo p/ deny-by-default (bloqueava web_fetch/write_file a
 *      sessão toda). Agora traduzimos: allow[] → allow_instructions; block[] → blockRules
 *      ESTRUTURADAS (tool+match exato, para NÃO super-bloquear via ruleMatches); e
 *      carregamos `default_deny` para o gate final decidir block (true) vs ask (false).
 *
 * IDEMPOTENTE: revalidar um value já traduzido preserva default_deny + blockRules
 * (autoReview revalida a policy que recebe). MALFORMADO → deny-by-default, NUNCA lança.
 * @param {unknown} input
 * @returns {{ ok: boolean, error: string|null, value: Permissions & { default_deny?: boolean, blockRules?: Array<{tool:string,match?:string,reason?:string}>, askTools?: string[] } }}
 */
export function validatePermissions(input) {
  const fallback = {
    ok: false,
    error: 'malformed permissions — falling back to deny-by-default',
    value: { autoRun: { allow_instructions: [], block_instructions: [] }, default_deny: undefined, blockRules: [], askTools: [] },
  };
  if (!input || typeof input !== 'object' || Array.isArray(input)) return fallback;
  const p = /** @type {any} */ (input);
  const clean = (arr) => (Array.isArray(arr) ? arr.filter((s) => typeof s === 'string' && s.trim().length > 0) : []);
  const toolNames = (arr) => (Array.isArray(arr) ? arr : [])
    .map((e) => (e && typeof e === 'object' ? e.tool : e))
    .filter((t) => typeof t === 'string' && t.trim().length > 0);

  const a = p.autoRun;

  // Schema 1 — NL-schema (or an already-translated value, which also carries autoRun).
  // Preserva default_deny/blockRules do input p/ ser idempotente na revalidação.
  if (a && typeof a === 'object' && !Array.isArray(a)) {
    return {
      ok: true,
      error: null,
      value: {
        autoRun: {
          allow_instructions: clean(a.allow_instructions),
          block_instructions: clean(a.block_instructions),
        },
        default_deny: coerceDefaultDeny(p.default_deny),
        blockRules: cleanBlockRules(p.blockRules),
        askTools: toolNames(p.askTools),
      },
    };
  }

  // Schema 2 — canonical tool-schema (Worker). Detecta pela presença de default_deny
  // ou de qualquer array allow/ask/block. Traduz para o modelo do renderer.
  const isToolSchema = typeof p.default_deny !== 'undefined'
    || Array.isArray(p.allow) || Array.isArray(p.ask) || Array.isArray(p.block);
  if (isToolSchema) {
    return {
      ok: true,
      error: null,
      value: {
        // `ask` tools NÃO entram em allow (não são auto-run). Vão para askTools: o gate
        // final retorna 'ask' para elas MESMO com default_deny:true (precedência canônica
        // block > ask > allow > default; antes eram descartadas e caíam em hard-block).
        autoRun: {
          allow_instructions: toolNames(p.allow),
          block_instructions: [],
        },
        default_deny: coerceDefaultDeny(p.default_deny),
        blockRules: cleanBlockRules(p.block),
        askTools: toolNames(p.ask),
      },
    };
  }

  return fallback;
}
